import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, Field } from "@/pages/Login";
import { Lock, Warning } from "@phosphor-icons/react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div data-testid="reset-no-token" className="text-center py-4">
          <Warning size={32} weight="thin" className="mx-auto text-[#6B1718] mb-3" />
          <h1 className="font-serif-display text-2xl text-[#0A192F]">Missing reset token.</h1>
          <p className="text-sm text-[#5C5F58] mt-2">Use the link from the email we sent you, or request a new one.</p>
          <Link
            to="/forgot-password"
            className="inline-block mt-6 text-xs text-[#1A3622] hover:underline font-mono-ledger uppercase tracking-[0.25em]"
          >
            Request a new link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        Account Recovery
      </div>
      <h1 className="font-serif-display text-4xl text-[#0A192F] leading-tight mb-1">Set a new password.</h1>
      <p className="text-sm text-[#5C5F58] mb-6">
        Choose a password at least 8 characters long. Existing sessions will be signed out.
      </p>

      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <Field icon={<Lock size={14} />} label="New password">
          <input
            data-testid="reset-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>
        <Field icon={<Lock size={14} />} label="Confirm new password">
          <input
            data-testid="reset-password-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>

        {error && (
          <div className="text-xs text-[#6B1718] bg-[#6B1718]/5 border border-[#6B1718]/30 px-3 py-2" data-testid="reset-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="reset-submit"
          className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60"
        >
          {submitting ? "Updating…" : "Set new password"}
        </button>

        <p className="text-xs text-[#5C5F58] mt-6 text-center">
          <Link to="/login" className="underline underline-offset-4 text-[#1A3622]" data-testid="reset-link-login">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
